import SkTechApiClient from "./base/SkTechApiClient";

export type ProjectFile = {
  id: number;
  fileName: string;
  name: string;
  cloudinaryPublicId: string;
  url: string;
  folderId: number | null;
  virtualPath: string;
  size: number;
  extension: string;
  lastModified: string;
  isFolder: boolean;
};

export type Breadcrumb = {
  folderId: number | null;
  name: string;
  path: string;
};

export type MoveFileRequest = {
  fileId: number;
  targetFolderId: number | null;
};

export type MoveFolderRequest = {
  folderId: number;
  targetParentFolderId: number | null;
};

export type BatchMoveRequest = {
  fileIds: number[];
  folderIds: number[];
  targetFolderId: number | null;
};

export default class ProjectFilesApiClient extends SkTechApiClient {
  public async getFilesInFolder(projectId: string, folderId: number | null = null): Promise<ProjectFile[]> {
    const url = folderId 
      ? `/api/ProjectFile/list/${projectId}?folderId=${folderId}`
      : `/api/ProjectFile/list/${projectId}`;
    
    console.log(`API Client: Getting files for project ${projectId}, folder ${folderId}`);
    
    return this.Get(url)
      .then((response) => {
        if (response?.status === 200 && Array.isArray(response.data)) {
          console.log(`API Client: Received ${response.data.length} items from server`);
          return response.data as ProjectFile[];
        }
        console.warn(`API Client: Unexpected response format or empty response`);
        return [];
      })
      .catch((err) => {
        console.error('Error fetching files:', err);
        throw err;
      });
  }

  public async getBreadcrumbs(projectId: string, folderId: number | null = null): Promise<Breadcrumb[]> {
    const url = folderId 
      ? `/api/ProjectFile/breadcrumbs/${projectId}?folderId=${folderId}`
      : `/api/ProjectFile/breadcrumbs/${projectId}`;
    
    return this.Get(url)
      .then((response) => {
        if (response?.status === 200 && Array.isArray(response.data)) {
          return response.data as Breadcrumb[];
        }
        return [];
      })
      .catch((err) => {
        console.error('Error fetching breadcrumbs:', err);
        return [];
      });
  }

  public async uploadFile(
    projectId: string, 
    file: File, 
    folderId: number | null = null
  ): Promise<ProjectFile | null> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projectId", projectId);
    if (folderId) {
      formData.append("folderId", folderId.toString());
    }
    
    console.log(`API Client: Uploading file ${file.name} to project ${projectId}, folder ${folderId}`);
    
    return this.Post(`/api/ProjectFile/upload`, formData)
      .then((response) => {
        if (response?.status === 200 && response.data) {
          console.log(`API Client: Upload successful`);
          return response.data as ProjectFile;
        }
        console.warn(`API Client: Upload failed or unexpected response format`);
        return null;
      })
      .catch((err) => {
        console.error('Error uploading file:', err);
        throw err;
      });
  }

  public async createFolder(
    projectId: string, 
    folderName: string, 
    parentFolderId: number | null = null
  ): Promise<ProjectFile | null> {
    const requestData = {
      projectId: parseInt(projectId),
      folderName: folderName,
      parentFolderId: parentFolderId
    };
    
    console.log(`API Client: Creating folder "${folderName}" in project ${projectId}`);
    
    return this.Post(`/api/ProjectFile/folder`, requestData)
      .then((response) => {
        if (response?.status === 200 && response.data) {
          console.log(`API Client: Folder created successfully`);
          return response.data as ProjectFile;
        }
        console.warn(`API Client: Folder creation failed`);
        return null;
      })
      .catch((err) => {
        console.error('Error creating folder:', err);
        throw err;
      });
  }

  public async moveFile(fileId: number, targetFolderId: number | null): Promise<boolean> {
    const request: MoveFileRequest = {
      fileId,
      targetFolderId
    };
    
    return this.Post(`/api/ProjectFile/move/file`, request)
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error moving file:', err);
        return false;
      });
  }

  public async moveFolder(folderId: number, targetParentFolderId: number | null): Promise<boolean> {
    const request: MoveFolderRequest = {
      folderId,
      targetParentFolderId
    };
    
    return this.Post(`/api/ProjectFile/move/folder`, request)
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error moving folder:', err);
        return false;
      });
  }

  public async batchMove(
    fileIds: number[], 
    folderIds: number[], 
    targetFolderId: number | null
  ): Promise<any> {
    const request: BatchMoveRequest = {
      fileIds,
      folderIds,
      targetFolderId
    };
    
    return this.Post(`/api/ProjectFile/move/batch`, request)
      .then((response) => {
        if (response?.status === 200) {
          return response.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error batch moving items:', err);
        throw err;
      });
  }

  public async renameFile(fileId: number, newName: string): Promise<boolean> {
    return this.Put(`/api/ProjectFile/rename/file/${fileId}`, { newName })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error renaming file:', err);
        return false;
      });
  }

  public async renameFolder(folderId: number, newName: string): Promise<boolean> {
    return this.Put(`/api/ProjectFile/rename/folder/${folderId}`, { newName })
      .then((response) => {
        return response?.status === 200;
      })
      .catch((err) => {
        console.error('Error renaming folder:', err);
        return false;
      });
  }

  public async deleteFile(fileId: number): Promise<boolean> {
    console.log(`API Client: Deleting file ${fileId}`);
    
    return this.Delete(`/api/ProjectFile/file/${fileId}`)
      .then((response) => {
        const success = response?.status === 200 || response?.status === 204;
        console.log(`API Client: File deletion ${success ? 'successful' : 'failed'}`);
        return success;
      })
      .catch((err) => {
        console.error('Error deleting file:', err);
        return false;
      });
  }

  public async deleteFolder(folderId: number, forceDelete: boolean = false): Promise<boolean> {
    console.log(`API Client: Deleting folder ${folderId}`);
    
    const url = forceDelete 
      ? `/api/ProjectFile/folder/${folderId}?forceDelete=true`
      : `/api/ProjectFile/folder/${folderId}`;
    
    return this.Delete(url)
      .then((response) => {
        const success = response?.status === 200 || response?.status === 204;
        console.log(`API Client: Folder deletion ${success ? 'successful' : 'failed'}`);
        return success;
      })
      .catch((err) => {
        console.error('Error deleting folder:', err);
        return false;
      });
  }

  public getDownloadUrl(fileId: number): string {
    return `/api/ProjectFile/download/${fileId}`;
  }
}